import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  PaginationState
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import { useGetData, GetHandler } from "@/hooks/services/use-query-hooks";
import { cn } from "@/lib/utils";

interface ApiResponse<TData> {
  data: TData[];
  totalItems: number;
}

interface SimpleTableProps<TData> {
  // API approach (default) - uses useGetData with TanStack Query
  api?: (params: any) => GetHandler<ApiResponse<TData>>;
  filterParams?: Record<string, any>;

  // Data approach - for static/pre-fetched data
  data?: TData[];
  totalItems?: number;

  // Common props
  columns: ColumnDef<TData>[];
  isLoading?: boolean;
  onPaginationChange?: (pagination: { page: number; pageSize: number }) => void;
  defaultPageSize?: number;
}

export function SimpleTable<TData>({
  api,
  filterParams = {},
  data: providedData,
  totalItems: providedTotalItems,
  columns,
  isLoading: externalLoading = false,
  onPaginationChange,
  defaultPageSize = 10
}: SimpleTableProps<TData>) {
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize
  });

  // API approach using useGetData (TanStack Query)
  const queryParams = React.useMemo(
    () => ({
      ...filterParams,
      page: pagination.pageIndex + 1,
      size: pagination.pageSize
    }),
    [filterParams, pagination.pageIndex, pagination.pageSize]
  );

  const apiQuery = useGetData(
    api
      ? api(queryParams)
      : { name: "disabled", handle: async () => ({ data: [], totalItems: 0 }) },
    { enabled: !!api }
  );

  // Determine which data source to use
  const tableData = api ? apiQuery.data?.data || [] : providedData || [];
  const totalItems = api
    ? apiQuery.data?.totalItems || 0
    : providedTotalItems || 0;
  const isLoading = api ? apiQuery.isLoading : externalLoading;

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    state: {
      pagination
    },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function" ? updater(pagination) : updater;
      setPagination(newPagination);
      onPaginationChange?.({
        page: newPagination.pageIndex + 1,
        pageSize: newPagination.pageSize
      });
    }
  });

  const startEntry = pagination.pageIndex * pagination.pageSize + 1;
  const currentPage = pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (isLoading) {
    return (
      <div className="aero:flex aero:items-center aero:justify-center aero:h-64">
        <div className="aero:animate-spin aero:w-8 aero:h-8 aero:border-4 aero:border-brand-primary-600 aero:border-t-transparent aero:rounded-full" />
      </div>
    );
  }

  return (
    <div className="aero:flex aero:flex-col aero:h-full">
      {/* Table */}
      <div className="aero:flex-1 aero:overflow-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="aero:border-b aero:border-neutral-400"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="aero:px-2 aero:py-2.5 aero:bg-white aero:sticky aero:top-0 aero:z-10"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="aero:border-b aero:border-neutral-400 aero:hover:bg-neutral-50 aero:transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="aero:px-2 aero:py-2.5">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="aero:h-32 aero:text-center aero:text-neutral-500"
                >
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="aero:flex aero:items-center aero:justify-between aero:px-4 aero:py-3 aero:border-t aero:border-neutral-200">
        {/* Left: Page Size Selector */}
        <div className="aero:flex aero:items-center aero:gap-4">
          <Select
            value={pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="aero:h-9 aero:w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="aero:text-base aero:text-neutral-900">
            {startEntry} of {totalItems} entries
          </span>
        </div>

        {/* Right: Page Numbers */}
        <div className="aero:flex aero:items-center aero:gap-6">
          {/* Previous Button */}
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className={cn(
              "aero:flex aero:items-center aero:gap-1 aero:text-base aero:text-neutral-500",
              !table.getCanPreviousPage() &&
                "aero:opacity-50 aero:cursor-not-allowed"
            )}
          >
            <ChevronLeft className="aero:w-5 aero:h-5" />
            Previous
          </button>

          {/* Page Numbers */}
          <div className="aero:flex aero:items-center aero:gap-2">
            {pageNumbers.map((pageNum, idx) => {
              const isActive = pageNum === currentPage;
              return (
                <button
                  key={idx}
                  onClick={() =>
                    typeof pageNum === "number" &&
                    table.setPageIndex(pageNum - 1)
                  }
                  disabled={typeof pageNum !== "number"}
                  className={cn(
                    "aero:h-9 aero:px-4 aero:py-2 aero:text-sm aero:border aero:rounded",
                    isActive
                      ? "aero:bg-brand-primary-50 aero:border-brand-primary-700 aero:text-brand-primary-700 aero:font-medium"
                      : "aero:bg-white aero:border-neutral-300 aero:text-neutral-500 hover:aero:bg-neutral-50"
                  )}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className={cn(
              "aero:flex aero:items-center aero:gap-1 aero:text-base aero:text-neutral-500",
              !table.getCanNextPage() &&
                "aero:opacity-50 aero:cursor-not-allowed"
            )}
          >
            Next
            <ChevronRight className="aero:w-5 aero:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
